import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiTrendingUp } from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN } from './siteConfig';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';

const PATH = '/keywords-explorer';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;

export default function KeywordsExplorerPage() {
  useEffect(() => {
    document.title = 'Keywords Explorer — search volume & competition | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : '';
    if (desc) desc.setAttribute('content', 'Free keyword explorer: enter seed keywords to see monthly search volume, competition level and CPC estimates. Powered by Google Ads Keyword Planner. WebCodeCraft.');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    const twDesc = document.querySelector('meta[property="twitter:description"]');
    const twUrl = document.querySelector('meta[property="twitter:url"]');
    const pageTitle = 'Keywords Explorer — search volume & competition | WebCodeCraft';
    const pageDesc = 'Free keyword explorer: enter seed keywords to see monthly search volume, competition level and CPC estimates. Powered by Google Ads Keyword Planner. WebCodeCraft.';
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);
    if (ogDesc) ogDesc.setAttribute('content', pageDesc);
    if (ogUrl) ogUrl.setAttribute('content', CANONICAL);
    if (twTitle) twTitle.setAttribute('content', pageTitle);
    if (twDesc) twDesc.setAttribute('content', pageDesc);
    if (twUrl) twUrl.setAttribute('content', CANONICAL);
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'WebPage',
      name: pageTitle, url: CANONICAL, description: pageDesc,
      isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
      publisher: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN }
    });
    ld.setAttribute('data-page', 'keywords-explorer');
    document.head.appendChild(ld);
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      if (desc && prevDesc != null) desc.setAttribute('content', prevDesc);
      ld.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      <CookieBanner />
      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip">
        <section className="container mx-auto px-4 py-10 max-w-3xl" aria-labelledby="kw-heading">
          <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-medium text-indigo-600 mb-2">
              <Link to="/check-your-seo" className="underline underline-offset-2 hover:text-indigo-800">Tools</Link>
              <span className="mx-1 text-slate-400" aria-hidden="true">/</span>
              <span className="text-slate-700">Keywords explorer</span>
            </p>
            <h1 id="kw-heading" className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight mb-4">
              Keywords explorer
            </h1>

            <m.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="relative my-10 rounded-2xl border-2 border-dashed border-indigo-300 bg-indigo-50/60 px-6 py-14 sm:py-20 text-center"
            >
              <FiTrendingUp className="mx-auto text-indigo-400 mb-4 opacity-90" size={48} aria-hidden="true" />
              <p
                className="font-stamp text-4xl sm:text-5xl md:text-6xl text-rose-900/85 mb-3 -rotate-[2deg] tracking-wide [text-shadow:0_1px_0_rgba(255,255,255,0.35),0_2px_4px_rgba(127,29,29,0.12)]"
              >
                Coming soon
              </p>
              <p className="text-slate-600 text-lg leading-relaxed max-w-md mx-auto mb-6">
                We're connecting the Google Ads Keyword Planner so you can explore search volume, competition and CPC for any keyword — completely free.
              </p>
              <p className="text-sm text-slate-500 max-w-sm mx-auto">
                In the meantime, try our other free tools:
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-4">
                <Link to="/check-your-seo" className="inline-flex items-center px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-800 hover:bg-slate-50 shadow-sm transition-colors">
                  SEO audit
                </Link>
                <Link to="/competitor-tracker" className="inline-flex items-center px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-800 hover:bg-slate-50 shadow-sm transition-colors">
                  Competitor tracker
                </Link>
                <Link to="/serp-overview" className="inline-flex items-center px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-800 hover:bg-slate-50 shadow-sm transition-colors">
                  SERP overview
                </Link>
                <Link to="/website-crawler" className="inline-flex items-center px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-800 hover:bg-slate-50 shadow-sm transition-colors">
                  Website crawler
                </Link>
              </div>
            </m.div>

          </m.div>
        </section>
        <Footer />
      </main>
    </div>
  );
}
